import { SerializedStyles } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Ref, memo, useCallback, useMemo } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useNoSubmit } from "src/apps/AppFlow/pages/hooks";
import { styles as GENERAL_STYLES } from "src/apps/AppFlow/pages/styles";
import { FormFlowPage, InfoAlert, TextInput, Touchable } from "src/components";
import { BoldText, Button, RegularText } from "src/components/v1";
import { LTV_BEFORE_ALERT } from "src/config/application";
import { useModal } from "src/hooks";
import useMergedRef from "src/hooks/useMergedRef";
import { UserPlus, X } from "src/icons";
import { createStyles } from "src/styles";
import { MortgageInformation } from "src/types/Applicant";
import { MortgageBalanceAndLenderValue } from "src/types/Application";
import { noInput } from "src/utilities/input";
import { v4 as uuid } from "uuid";
import * as z from "zod";

import { colors, formatters, parsers } from "@fraction/shared";

export interface PrimaryPropertyMortgagesProps {
  onChange: (value: MortgageInformation[]) => void;
  value?: MortgageInformation[];
  propertyAddress?: string;
  rate?: parsers.rate.Rate;
  propertyValue?: number;

  focusRef?: Ref<HTMLInputElement>;
  onNext: () => void;
  onBack: () => void;
  style?: SerializedStyles;
}

interface MortgageInformationContactProps {
  onChange: (index: number, value: MortgageBalanceAndLenderValue) => void;
  onDelete?: (index: number) => void;
  value?: MortgageBalanceAndLenderValue;
  index: number;
  errors?: FieldErrors<any>;

  focusRef?: Ref<HTMLInputElement>;
  register: Ref<HTMLInputElement>;
}

const styles = createStyles({
  alert: {
    marginBottom: 40,
  },
  container: {
    marginBottom: 24,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  nowrap: {
    flexWrap: "nowrap",
  },
  button: {
    marginLeft: 20,
    // This is to make up for the fact that the inputs have lots of "blank"
    // space at the bottom which makes the button look un-aligned without this margin.
    marginBottom: 30,
  },
  input: {
    width: "fit-content",
    minWidth: 265,
    "@media(max-width: 800px)": {
      width: "100%",
    },
  },
  inputContainer: {
    "&:not(:last-child)": {
      marginRight: 20,
      "@media(max-width: 800px)": {
        marginRight: 0,
      },
    },
    "@media(max-width: 800px)": {
      width: "100%",
    },
  },
});

const RAW_STYLES = {
  userPlus: {
    marginLeft: 11,
  },
};

const MortgageInformationContact = memo(
  ({ errors, index, onChange, onDelete, value, register, focusRef }: MortgageInformationContactProps) => {
    const merged = useMergedRef(register, focusRef);
    const ref = focusRef ? merged : register;

    const handleChangeAmount = useCallback(
      (amount: string) => {
        onChange(index, {
          ...value,
          amount,
        });
      },
      [index, value, onChange]
    );

    const handleChangeLenderName = useCallback(
      (lenderName: string) => {
        onChange(index, {
          ...value,
          lenderName,
        });
      },
      [index, value, onChange]
    );

    const handleDelete = useCallback(() => {
      onDelete?.(index);
    }, [index, onDelete]);

    return (
      <div css={[styles.row, styles.nowrap]}>
        <div css={styles.row}>
          <TextInput
            error={errors?.lenderName?.message}
            ref={ref}
            inputStyle={styles.input}
            style={[GENERAL_STYLES.input, styles.inputContainer]}
            name={`lenderName-${index}`}
            label="Bank or lender name"
            placeholder="e.g. Fraction"
            defaultValue={value?.lenderName || ""}
            onChange={handleChangeLenderName}
            autoComplete="lenderName"
          />
          <TextInput
            error={errors?.balance?.message}
            inputStyle={styles.input}
            style={[GENERAL_STYLES.input, styles.inputContainer]}
            name={`amount-${index}`}
            label="Mortgage balance"
            defaultValue={value?.amount || ""}
            onChange={handleChangeAmount}
            min={0}
            max={100000000}
            maskOptions={{
              rightAlign: false,
              alias: "currency",
              prefix: "$ ",
              jitMasking: false,
              digits: 0,
            }}
            ref={register}
          />
        </div>
        <Touchable css={styles.button} onClick={handleDelete}>
          <X height={28} width={28} color={colors.ICON} />
        </Touchable>
      </div>
    );
  }
);

const PrimaryPropertyMortgages = ({
  propertyAddress,
  rate,
  propertyValue,
  onNext,
  onBack,
  style,
  focusRef,
  value,
  onChange,
  ...props
}: PrimaryPropertyMortgagesProps) => {
  const validate = useCallback(
    () =>
      !value?.some(
        ({ mortgageBalanceAndLender }) =>
          // Can't advance if partially filled
          noInput(mortgageBalanceAndLender?.lenderName) !== noInput(mortgageBalanceAndLender?.amount)
      ),
    [value]
  );
  const { submitHandler, nextDisabled } = useNoSubmit({ onNext, validate });

  const schema = useMemo(() => {
    const schemaShape: any = {};
    for (let index = 0; index < (value?.length || 0); index++) {
      schemaShape[`amount-${index}`] = z.string();
      schemaShape[`lenderName-${index}`] = z.string();
    }
    return z.object(schemaShape);
  }, [value?.length]);

  const { register, handleSubmit, errors } = useForm({
    resolver: zodResolver(schema),
  });

  const handleChangeMortgage = useCallback(
    (index: number, mortgage: MortgageBalanceAndLenderValue) => {
      const newPrimaryPropertyMortgages = [...(value || [])];
      newPrimaryPropertyMortgages[index] = {
        ...newPrimaryPropertyMortgages[index],
        mortgageBalanceAndLender: {
          ...newPrimaryPropertyMortgages[index].mortgageBalanceAndLender,
          ...mortgage,
        },
      };
      onChange?.(newPrimaryPropertyMortgages);
    },
    [value, onChange]
  );

  const handleAddMortgage = useCallback(() => {
    onChange([...(value || []), { id: uuid() }]);
  }, [value, onChange]);

  const handleDeleteMortgage = useCallback(
    (index: number) => {
      const newPrimaryPropertyMortgages = [...(value || [])];
      newPrimaryPropertyMortgages?.splice(index, 1);
      onChange(newPrimaryPropertyMortgages);
    },
    [value, onChange]
  );

  const { showModal, closeModal } = useModal();

  const handleOnNext = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      e.preventDefault();

      // Remove any empty mortgages
      onChange([...(value || [])]?.filter((mtg) => mtg.mortgageBalanceAndLender?.lenderName?.trim()));

      const summedValue =
        value?.reduce(
          (acc, mtg) => acc + formatters.number.getCents(mtg.mortgageBalanceAndLender?.amount || 0),
          0
        ) || 0;
      const ltv = summedValue / (propertyValue || 1);
      const rateLtv = rate?.maximumLTV || LTV_BEFORE_ALERT;
      if (ltv > rateLtv) {
        showModal({
          message: (
            <div>
              <BoldText className="text-xl">Your loan-to-value ratio may be too high</BoldText>
              <RegularText style={{ marginTop: "0.5rem" }}>
                Given your property value estimate of{" "}
                <BoldText>{formatters.number.getCurrencyWords(propertyValue)}</BoldText> and your mortgage
                balance of <BoldText>{formatters.number.getCurrencyWords(summedValue)}</BoldText>, your
                loan-to-value ratio is{" "}
                <BoldText>
                  {formatters.number.getPercentageFromDecimal(ltv, {
                    digits: 0,
                  })}
                </BoldText>
                . Fraction has a maximum allowed loan-to-value ratio of{" "}
                <BoldText>
                  {formatters.number.getPercentageFromDecimal(rateLtv, {
                    digits: 0,
                  })}
                </BoldText>
                .
                <RegularText style={{ marginTop: "0.5rem" }}>
                  You can still continue with your application, but please note that Fraction does not
                  currently offer second mortgages.
                </RegularText>
              </RegularText>
              <InfoAlert className="mt-3">
                A loan-to-value (LTV) ratio is the size of the mortgage on the home relative to the property
                value. If your home is worth $600k and you have $300k left on your mortgage, your LTV is 50%.
              </InfoAlert>
            </div>
          ),
          actions: [
            {
              text: "Cancel",
              action: closeModal,
              type: "secondary",
            },
            {
              text: "I understand. Continue",
              action: () => {
                handleSubmit(submitHandler)();
                closeModal();
              },
            },
          ],
        });
      } else {
        handleSubmit(submitHandler)();
      }
    },
    [handleSubmit, submitHandler, value, propertyValue, showModal, rate?.maximumLTV, onChange]
  );

  return (
    <FormFlowPage
      {...props}
      title={`Please list all of the mortgages on ${propertyAddress || "your property"}.`}
      description="You should include all mortgages, home equity lines of credit, or other liens on the property."
      onNext={handleOnNext}
      onBack={onBack}
      buttonText={!value?.length ? "Property has no mortgages" : "Next"}
      overflowTextFallback={!value?.length ? "Skip" : "Next"}
      style={style}
      ref={focusRef}
      nextDisabled={nextDisabled}
    >
      <div css={styles.container}>
        <>
          {value?.map((mortgage, index) => (
            <MortgageInformationContact
              key={mortgage.id ?? index}
              register={register}
              focusRef={index === 0 ? focusRef : undefined}
              errors={{
                lenderName: errors?.[`lenderName-${index}`],
                amount: errors?.[`amount-${index}`],
              }}
              value={mortgage?.mortgageBalanceAndLender}
              index={index}
              onDelete={handleDeleteMortgage}
              onChange={handleChangeMortgage}
            />
          ))}
        </>
        <Button narrow onClick={handleAddMortgage}>
          Add mortgage
          <UserPlus style={RAW_STYLES.userPlus} color="white" height={18} width={18} />
        </Button>
      </div>
      {value?.length ? (
        <InfoAlert style={styles.alert}>
          Note that Fraction will require that you pay off all existing mortgages on the subject property with
          the proceeds from the Fraction loan.
        </InfoAlert>
      ) : null}
    </FormFlowPage>
  );
};

export default memo(PrimaryPropertyMortgages);
